import React, { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Button, Form, Tab, Tabs } from "react-bootstrap";
import { Context } from "../App";
import AdminLayout from "../components/AdminLayout";
import AdminHomeAdvancedSections from "../components/AdminHomeAdvancedSections";
import AdminSitePageMediaPanel from "../components/AdminSitePageMediaPanel";
import { useCms } from "../context/CmsContext";
import { loadMergedCms } from "../utils/cmsStorage";
import { normalizeTheaterFolderKey } from "../utils/theaterGalleryImages";
import { pushSiteCmsToServer } from "../utils/cmsBootstrap";
import "../adminConsoleTheme.css";

const SITE_CONTENT_TABS = new Set(["home", "theatres", "page-media", "booking", "tools"]);

function cloneDeep(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function AdminSiteContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { context } = useContext(Context);
  const { cms, patchCms, reset, exportJson, importJson, refresh } = useCms();

  const activeTabKey = useMemo(() => {
    const t = searchParams.get("tab");
    return SITE_CONTENT_TABS.has(t) ? t : "home";
  }, [searchParams]);

  const setActiveTabKey = useCallback((nextKey) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (!nextKey || nextKey === "home") {
          p.delete("tab");
        } else {
          p.set("tab", nextKey);
        }
        return p;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const [homeDraft, setHomeDraft] = useState(() => cloneDeep(cms.home));
  const [mediaDraft, setMediaDraft] = useState(() => cloneDeep(cms.theaterMedia || {}));
  const [bookingDraft, setBookingDraft] = useState(() => cloneDeep(cms.booking));
  const [bookingFormDraft, setBookingFormDraft] = useState(() =>
    cloneDeep(cms.bookingForm)
  );
  const [decorationsDraft, setDecorationsDraft] = useState(() =>
    cloneDeep(cms.decorations || {})
  );
  const [newTheaterKey, setNewTheaterKey] = useState("");
  const [importText, setImportText] = useState("");
  const [banner, setBanner] = useState(null);

  React.useEffect(() => {
    if (!context.isAdminLoggedIn) {
      navigate("/admin", { replace: true });
      return;
    }
    if (String(context.adminRole || "").toUpperCase() !== "SUPER_ADMIN") {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [context.isAdminLoggedIn, context.adminRole, navigate]);

  const showBanner = useCallback((variant, message) => {
    setBanner({ variant, message });
    window.setTimeout(() => setBanner(null), 5000);
  }, []);

  const syncSiteFile = useCallback(async () => {
    try {
      await pushSiteCmsToServer(loadMergedCms(), context.adminRole);
      return true;
    } catch (_) {
      return false;
    }
  }, [context.adminRole]);

  const saveHome = async () => {
    const next = patchCms({ home: homeDraft });
    setHomeDraft(cloneDeep(next.home));
    const ok = await syncSiteFile();
    showBanner(
      ok ? "success" : "warning",
      ok
        ? "Home content saved and shared CMS file updated."
        : "Home content saved locally — could not sync shared CMS file (check server / SUPER_ADMIN)."
    );
  };

  const saveTheaterMedia = async () => {
    const next = patchCms({ theaterMedia: mediaDraft });
    setMediaDraft(cloneDeep(next.theaterMedia || {}));
    const ok = await syncSiteFile();
    showBanner(
      ok ? "success" : "warning",
      ok
        ? "Theatre media saved and shared CMS file updated."
        : "Theatre media saved locally — could not sync shared CMS file."
    );
  };

  const saveBookingCopy = async () => {
    const next = patchCms({
      booking: bookingDraft,
      bookingForm: bookingFormDraft,
      decorations: decorationsDraft,
    });
    setBookingDraft(cloneDeep(next.booking));
    setBookingFormDraft(cloneDeep(next.bookingForm));
    setDecorationsDraft(cloneDeep(next.decorations || {}));
    const ok = await syncSiteFile();
    showBanner(
      ok ? "success" : "warning",
      ok
        ? "Booking copy saved and shared CMS file updated."
        : "Booking copy saved locally — could not sync shared CMS file."
    );
  };

  /** Page media edits the same Home slice — same save + CMS file sync path. */
  const savePageMediaTab = async () => {
    await saveHome();
  };

  const reloadAllDrafts = useCallback(() => {
    refresh();
    const latest = loadMergedCms();
    setHomeDraft(cloneDeep(latest.home));
    setMediaDraft(cloneDeep(latest.theaterMedia || {}));
    setBookingDraft(cloneDeep(latest.booking));
    setBookingFormDraft(cloneDeep(latest.bookingForm));
    setDecorationsDraft(cloneDeep(latest.decorations || {}));
    showBanner("info", "Forms reloaded from last saved CMS.");
  }, [refresh, showBanner]);

  const setHero = (patch) => {
    setHomeDraft((prev) => ({
      ...prev,
      hero: { ...prev.hero, ...patch },
    }));
  };

  const setPromoBanner = (patch) => {
    setHomeDraft((prev) => ({
      ...prev,
      promoBanner: { ...(prev.promoBanner || {}), ...patch },
    }));
  };

  const setPromoPopup = (patch) => {
    setHomeDraft((prev) => ({
      ...prev,
      promoPopup: { ...(prev.promoPopup || {}), ...patch },
    }));
  };

  const setSocialLinks = (patch) => {
    setHomeDraft((prev) => ({
      ...prev,
      socialLinks: { ...(prev.socialLinks || {}), ...patch },
    }));
  };

  const setFoodSection = (patch) => {
    setHomeDraft((prev) => ({
      ...prev,
      foodSection: { ...prev.foodSection, ...patch },
    }));
  };

  const setFinalCta = (patch) => {
    setHomeDraft((prev) => ({
      ...prev,
      finalCta: { ...prev.finalCta, ...patch },
    }));
  };

  const setFooterBlock = (patch) => {
    setHomeDraft((prev) => ({
      ...prev,
      footer: { ...prev.footer, ...patch },
    }));
  };

  const updateMediaEntry = (key, patch) => {
    setMediaDraft((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), ...patch },
    }));
  };

  const parseImagesTextarea = (text) =>
    text
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

  const addTheaterRow = () => {
    const key = normalizeTheaterFolderKey(newTheaterKey);
    if (!key) {
      showBanner("warning", "Enter a theatre name or folder key (e.g. Mercury).");
      return;
    }
    if (mediaDraft[key]) {
      showBanner("warning", `Entry "${key}" already exists.`);
      return;
    }
    setMediaDraft((prev) => ({
      ...prev,
      [key]: { images: [], theatreVideoUrl: "", branchVideoUrl: "" },
    }));
    setNewTheaterKey("");
    showBanner("success", `Added row for ${key}. Add URLs and save.`);
  };

  const removeTheaterRow = (key) => {
    setMediaDraft((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const appendImagesFromFiles = async (key, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const urls = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) continue;
      urls.push(await readFileAsDataUrl(f));
    }
    if (!urls.length) {
      showBanner("info", "No image files selected.");
      return;
    }
    setMediaDraft((prev) => {
      const entry = prev[key] || {};
      return {
        ...prev,
        [key]: {
          ...entry,
          images: [...(entry.images || []), ...urls],
        },
      };
    });
    showBanner(
      "info",
      `Added ${urls.length} image(s) as inline data. Prefer hosted URLs for large files.`
    );
  };

  return (
    <AdminLayout title="Site content">
      {banner ? (
        <Alert variant={banner.variant} className="mb-3">
          {banner.message}
        </Alert>
      ) : null}

      <div className="d-flex flex-wrap gap-2 mb-4">
        <Button variant="outline-secondary" type="button" onClick={reloadAllDrafts}>
          Reload forms from saved CMS
        </Button>
        <Button
          variant="outline-danger"
          type="button"
          onClick={async () => {
            if (
              window.confirm(
                "Reset entire site content to factory defaults? This cannot be undone."
              )
            ) {
              reset();
              const latest = loadMergedCms();
              setHomeDraft(cloneDeep(latest.home));
              setMediaDraft(cloneDeep(latest.theaterMedia || {}));
              setBookingDraft(cloneDeep(latest.booking));
              setBookingFormDraft(cloneDeep(latest.bookingForm));
              setDecorationsDraft(cloneDeep(latest.decorations || {}));
              const ok = await syncSiteFile();
              showBanner(
                ok ? "success" : "warning",
                ok
                  ? "CMS reset to defaults and shared CMS file updated."
                  : "CMS reset locally — shared CMS file sync failed."
              );
            }
          }}
        >
          Reset all to defaults
        </Button>
      </div>

      <Tabs
        activeKey={activeTabKey}
        onSelect={(k) => {
          if (k) setActiveTabKey(k);
        }}
        id="site-content-admin-tabs"
        className="mb-3"
      >
        <Tab eventKey="home" title="Home">
          <div className="admin-console-panel p-3 p-md-4 mt-3">
            <h5 className="mb-3">Header, promotions &amp; contact</h5>
            <Form.Group className="mb-3">
              <Form.Label>WhatsApp number (digits, country code, no +)</Form.Label>
              <Form.Control
                value={homeDraft.whatsappNumber || ""}
                onChange={(e) =>
                  setHomeDraft((p) => ({ ...p, whatsappNumber: e.target.value }))
                }
              />
            </Form.Group>

            <h6 className="text-secondary mb-2">Social links (header + footer icons)</h6>
            <p className="text-muted small mb-3">
              Full URLs, including https://. Leave blank to hide an icon.
            </p>
            <Form.Group className="mb-2">
              <Form.Label>Instagram</Form.Label>
              <Form.Control
                value={homeDraft.socialLinks?.instagram || ""}
                onChange={(e) => setSocialLinks({ instagram: e.target.value })}
                placeholder="https://www.instagram.com/_party_boss/"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>YouTube</Form.Label>
              <Form.Control
                value={homeDraft.socialLinks?.youtube || ""}
                onChange={(e) => setSocialLinks({ youtube: e.target.value })}
                placeholder="https://www.youtube.com/@PartyBoss93"
              />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label>Facebook</Form.Label>
              <Form.Control
                value={homeDraft.socialLinks?.facebook || ""}
                onChange={(e) => setSocialLinks({ facebook: e.target.value })}
                placeholder="https://www.facebook.com/61569713386163/"
              />
            </Form.Group>

            <h5 className="mb-3 mt-4">Promotions</h5>
            <p className="text-muted small mb-3">
              Edit offers anytime — banner scrolls in the white header (gradient matches Book now).
              Popup opens shortly after visitors land (like{" "}
              <a href="https://jollydistrict.com/" target="_blank" rel="noreferrer">
                Jolly District
              </a>
              ).
            </p>

            <h6 className="text-secondary">Header promo banner</h6>
            <Form.Check
              type="switch"
              id="promo-banner-enabled"
              className="mb-2"
              label="Show promo banner"
              checked={homeDraft.promoBanner?.enabled !== false}
              onChange={(e) => setPromoBanner({ enabled: e.target.checked })}
            />
            <Form.Group className="mb-3">
              <Form.Label>Main message (scrolls right → left)</Form.Label>
              <Form.Control
                value={homeDraft.promoBanner?.text || ""}
                onChange={(e) => setPromoBanner({ text: e.target.value })}
                placeholder="Rush!! Get flat discount of ₹300/-"
              />
            </Form.Group>

            <h6 className="text-secondary">Welcome popup</h6>
            <Form.Check
              type="switch"
              id="promo-popup-enabled"
              className="mb-2"
              label="Show popup on site load"
              checked={homeDraft.promoPopup?.enabled !== false}
              onChange={(e) => setPromoPopup({ enabled: e.target.checked })}
            />
            <Form.Check
              type="switch"
              id="promo-popup-once"
              className="mb-3"
              label="Only once per browser session (after Close / Book)"
              checked={homeDraft.promoPopup?.showOncePerSession !== false}
              onChange={(e) => setPromoPopup({ showOncePerSession: e.target.checked })}
            />
            <Form.Group className="mb-2">
              <Form.Label>Popup eyebrow (small line above title)</Form.Label>
              <Form.Control
                value={homeDraft.promoPopup?.eyebrow || ""}
                onChange={(e) => setPromoPopup({ eyebrow: e.target.value })}
                placeholder="✨ Exclusive at Party Boss"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Popup title</Form.Label>
              <Form.Control
                value={homeDraft.promoPopup?.title || ""}
                onChange={(e) => setPromoPopup({ title: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Popup message</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={homeDraft.promoPopup?.body || ""}
                onChange={(e) => setPromoPopup({ body: e.target.value })}
              />
            </Form.Group>
            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-2">
                  <Form.Label>Primary button</Form.Label>
                  <Form.Control
                    value={homeDraft.promoPopup?.ctaLabel || ""}
                    onChange={(e) => setPromoPopup({ ctaLabel: e.target.value })}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-2">
                  <Form.Label>Dismiss button</Form.Label>
                  <Form.Control
                    value={homeDraft.promoPopup?.dismissLabel || ""}
                    onChange={(e) => setPromoPopup({ dismissLabel: e.target.value })}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>CTA route (hash path)</Form.Label>
                  <Form.Control
                    value={homeDraft.promoPopup?.ctaPath || ""}
                    onChange={(e) => setPromoPopup({ ctaPath: e.target.value })}
                    placeholder="/booknow"
                  />
                </Form.Group>
              </div>
            </div>

            <h5 className="mb-3 mt-4">Hero &amp; media</h5>
            <Form.Group className="mb-2">
              <Form.Label>Hero background video URL</Form.Label>
              <Form.Control
                value={homeDraft.hero?.backgroundVideoUrl || ""}
                onChange={(e) => setHero({ backgroundVideoUrl: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Hero card video URL</Form.Label>
              <Form.Control
                value={homeDraft.hero?.cardVideoUrl || ""}
                onChange={(e) => setHero({ cardVideoUrl: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Badge</Form.Label>
              <Form.Control
                value={homeDraft.hero?.badge || ""}
                onChange={(e) => setHero({ badge: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Title (before gradient)</Form.Label>
              <Form.Control
                value={homeDraft.hero?.titleBeforeGradient || ""}
                onChange={(e) => setHero({ titleBeforeGradient: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Title (gradient part)</Form.Label>
              <Form.Control
                value={homeDraft.hero?.titleGradient || ""}
                onChange={(e) => setHero({ titleGradient: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Subtitle</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={homeDraft.hero?.subtitle || ""}
                onChange={(e) => setHero({ subtitle: e.target.value })}
              />
            </Form.Group>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-2">
                  <Form.Label>Primary CTA</Form.Label>
                  <Form.Control
                    value={homeDraft.hero?.primaryCta || ""}
                    onChange={(e) => setHero({ primaryCta: e.target.value })}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-2">
                  <Form.Label>Secondary CTA</Form.Label>
                  <Form.Control
                    value={homeDraft.hero?.secondaryCta || ""}
                    onChange={(e) => setHero({ secondaryCta: e.target.value })}
                  />
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mb-2">
              <Form.Label>Hero card badge</Form.Label>
              <Form.Control
                value={homeDraft.hero?.heroCardBadge || ""}
                onChange={(e) => setHero({ heroCardBadge: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Hero card title</Form.Label>
              <Form.Control
                value={homeDraft.hero?.heroCardTitle || ""}
                onChange={(e) => setHero({ heroCardTitle: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Hero card text</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={homeDraft.hero?.heroCardText || ""}
                onChange={(e) => setHero({ heroCardText: e.target.value })}
              />
            </Form.Group>

            <h5 className="mb-3">Food block</h5>
            <Form.Group className="mb-2">
              <Form.Label>Food section image URL</Form.Label>
              <Form.Control
                value={homeDraft.foodSection?.imageUrl || ""}
                onChange={(e) => setFoodSection({ imageUrl: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Upload image (embeds as data URL — keep files small)</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f) return;
                  const url = await readFileAsDataUrl(f);
                  setFoodSection({ imageUrl: url });
                }}
              />
            </Form.Group>

            <h5 className="mb-3">Final CTA strip</h5>
            <Form.Group className="mb-2">
              <Form.Label>Background image URL</Form.Label>
              <Form.Control
                value={homeDraft.finalCta?.bgImageUrl || ""}
                onChange={(e) => setFinalCta({ bgImageUrl: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={homeDraft.finalCta?.title || ""}
                onChange={(e) => setFinalCta({ title: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Subtitle</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={homeDraft.finalCta?.subtitle || ""}
                onChange={(e) => setFinalCta({ subtitle: e.target.value })}
              />
            </Form.Group>
            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-2">
                  <Form.Label>Primary button</Form.Label>
                  <Form.Control
                    value={homeDraft.finalCta?.primaryLabel || ""}
                    onChange={(e) => setFinalCta({ primaryLabel: e.target.value })}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-2">
                  <Form.Label>WhatsApp button</Form.Label>
                  <Form.Control
                    value={homeDraft.finalCta?.whatsappLabel || ""}
                    onChange={(e) => setFinalCta({ whatsappLabel: e.target.value })}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-2">
                  <Form.Label>WhatsApp preset message</Form.Label>
                  <Form.Control
                    value={homeDraft.finalCta?.whatsappPreset || ""}
                    onChange={(e) => setFinalCta({ whatsappPreset: e.target.value })}
                  />
                </Form.Group>
              </div>
            </div>

            <h5 className="mb-3">Footer</h5>
            <Form.Group className="mb-2">
              <Form.Label>Footer title</Form.Label>
              <Form.Control
                value={homeDraft.footer?.title || ""}
                onChange={(e) => setFooterBlock({ title: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Footer body</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={homeDraft.footer?.body || ""}
                onChange={(e) => setFooterBlock({ body: e.target.value })}
              />
            </Form.Group>
            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-2">
                  <Form.Label>Book button</Form.Label>
                  <Form.Control
                    value={homeDraft.footer?.bookLabel || ""}
                    onChange={(e) => setFooterBlock({ bookLabel: e.target.value })}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-2">
                  <Form.Label>WhatsApp button</Form.Label>
                  <Form.Control
                    value={homeDraft.footer?.whatsappLabel || ""}
                    onChange={(e) =>
                      setFooterBlock({ whatsappLabel: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-2">
                  <Form.Label>Copyright name</Form.Label>
                  <Form.Control
                    value={homeDraft.footer?.copyrightName || ""}
                    onChange={(e) =>
                      setFooterBlock({ copyrightName: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mb-2">
              <Form.Label>Brand blurb</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={homeDraft.footer?.partyBossBlurb || ""}
                onChange={(e) =>
                  setFooterBlock({ partyBossBlurb: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Contact lines (one per line)</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={(homeDraft.footer?.contactLines || []).join("\n")}
                onChange={(e) =>
                  setFooterBlock({
                    contactLines: e.target.value
                      .split("\n")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Floating book button label</Form.Label>
              <Form.Control
                value={homeDraft.floatingBookLabel || ""}
                onChange={(e) =>
                  setHomeDraft((p) => ({ ...p, floatingBookLabel: e.target.value }))
                }
              />
            </Form.Group>

            <AdminHomeAdvancedSections
              homeDraft={homeDraft}
              setHomeDraft={setHomeDraft}
              showBanner={showBanner}
            />

            <Button variant="primary" type="button" onClick={saveHome}>
              Save home
            </Button>
          </div>
        </Tab>

        <Tab eventKey="theatres" title="Theatre list media">
          <div className="admin-console-panel p-3 p-md-4 mt-3">
            <p className="small text-muted">
              Keys must match normalized theatre names (e.g. <code>MERCURY</code>,{" "}
              <code>GALAXY</code>). Same keys apply on the booking flow cards. Images: one URL per
              line, or upload small images (data URLs).
            </p>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Form.Control
                placeholder="Theatre name or key"
                style={{ maxWidth: 280 }}
                value={newTheaterKey}
                onChange={(e) => setNewTheaterKey(e.target.value)}
              />
              <Button variant="outline-primary" type="button" onClick={addTheaterRow}>
                Add theatre row
              </Button>
            </div>

            {Object.keys(mediaDraft).length === 0 ? (
              <p className="text-muted">No theatre media rows yet.</p>
            ) : null}

            {Object.entries(mediaDraft).map(([key, entry]) => (
              <div
                key={key}
                className="border rounded p-3 mb-3"
                style={{ borderColor: "rgba(148,163,184,.35)" }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>{key}</strong>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    type="button"
                    onClick={() => removeTheaterRow(key)}
                  >
                    Remove
                  </Button>
                </div>
                <Form.Group className="mb-2">
                  <Form.Label>Theatre video URL</Form.Label>
                  <Form.Control
                    value={entry?.theatreVideoUrl || ""}
                    onChange={(e) =>
                      updateMediaEntry(key, { theatreVideoUrl: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Branch video URL</Form.Label>
                  <Form.Control
                    value={entry?.branchVideoUrl || ""}
                    onChange={(e) =>
                      updateMediaEntry(key, { branchVideoUrl: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Image URLs (one per line)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    className="font-monospace small"
                    value={(entry?.images || []).join("\n")}
                    onChange={(e) =>
                      updateMediaEntry(key, { images: parseImagesTextarea(e.target.value) })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label>Upload images</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      appendImagesFromFiles(key, e.target.files);
                      e.target.value = "";
                    }}
                  />
                </Form.Group>
              </div>
            ))}

            <Button variant="primary" type="button" onClick={saveTheaterMedia}>
              Save theatre media
            </Button>
          </div>
        </Tab>

        <Tab eventKey="page-media" title="Page media">
          <div className="admin-console-panel p-3 p-md-4 mt-3">
            <AdminSitePageMediaPanel
              homeDraft={homeDraft}
              setHomeDraft={setHomeDraft}
              showBanner={showBanner}
              onSave={savePageMediaTab}
              onReload={reloadAllDrafts}
            />
          </div>
        </Tab>

        <Tab eventKey="booking" title="Booking copy">
          <div className="admin-console-panel p-3 p-md-4 mt-3">
            <h6>Select theatre step</h6>
            {Object.keys(bookingDraft).map((k) => (
              <Form.Group key={k} className="mb-2">
                <Form.Label className="small text-muted mb-0">{k}</Form.Label>
                <Form.Control
                  value={bookingDraft[k] ?? ""}
                  onChange={(e) =>
                    setBookingDraft((p) => ({ ...p, [k]: e.target.value }))
                  }
                />
              </Form.Group>
            ))}

            <h6 className="mt-4">Booking form step</h6>
            {Object.keys(bookingFormDraft).map((k) => (
              <Form.Group key={k} className="mb-2">
                <Form.Label className="small text-muted mb-0">{k}</Form.Label>
                <Form.Control
                  value={bookingFormDraft[k] ?? ""}
                  onChange={(e) =>
                    setBookingFormDraft((p) => ({ ...p, [k]: e.target.value }))
                  }
                />
              </Form.Group>
            ))}

            <h6 className="mt-4">Decorations snippet</h6>
            {Object.keys(decorationsDraft).map((k) => (
              <Form.Group key={k} className="mb-2">
                <Form.Label className="small text-muted mb-0">{k}</Form.Label>
                <Form.Control
                  value={decorationsDraft[k] ?? ""}
                  onChange={(e) =>
                    setDecorationsDraft((p) => ({ ...p, [k]: e.target.value }))
                  }
                />
              </Form.Group>
            ))}

            <Button
              variant="primary"
              type="button"
              className="mt-3"
              onClick={saveBookingCopy}
            >
              Save booking copy
            </Button>
          </div>
        </Tab>

        <Tab eventKey="tools" title="Import / export">
          <div className="admin-console-panel p-3 p-md-4 mt-3">
            <Form.Group className="mb-3">
              <Form.Label>Full CMS JSON</Form.Label>
              <Form.Control
                as="textarea"
                rows={12}
                className="font-monospace small"
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder="Paste exported JSON…"
              />
            </Form.Group>
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="outline-primary"
                type="button"
                onClick={() => {
                  setImportText(exportJson());
                  showBanner("info", "Exported current CMS into the textarea.");
                }}
              >
                Export to textarea
              </Button>
              <Button
                variant="primary"
                type="button"
                onClick={async () => {
                  try {
                    importJson(importText);
                    reloadAllDrafts();
                    const ok = await syncSiteFile();
                    showBanner(
                      ok ? "success" : "warning",
                      ok
                        ? "Imported CMS and updated shared CMS file."
                        : "Imported locally — shared CMS file sync failed."
                    );
                  } catch (e) {
                    showBanner("danger", `Import failed: ${e.message}`);
                  }
                }}
              >
                Import from textarea
              </Button>
            </div>
          </div>
        </Tab>
      </Tabs>
    </AdminLayout>
  );
}
